import { ScrollFocusOptions } from 'antd/es/form/interface';
import dayjs from 'dayjs';
import { API } from 'network/api';
import { routes } from 'routes/AllRoutes';
import { FarmerGroupLeader, PaddyBuyer, StakeholderStatus } from 'types/common';
import { FarmerGroupLeaderPayload, PaddyBuyerPayload } from 'types/request';
import { VALIDATION_ERROR_CODES } from 'utils/constants';
import {
  getCountryPhoneCode,
  getStakeholderTypeForCountry,
} from 'utils/helpers';
import i18n from 'utils/i18n';
import { MachineOwner } from './hooks/queries/useMachineOwnerQuery';
import { MachineOwnerFormObject } from './components/form/machine-owner';
import { message, UploadFile } from 'antd';

const { t } = i18n.i18n;

export const getWhatsappStatus = (status: string | undefined) => {
  const isVerified =
    status === 'VERIFIED_SENT' ||
    status === 'VERIFIED_READ' ||
    status === 'VERIFIED';
  const waStatus: {
    label: string;
    statusColor: 'green' | 'red';
  } = {
    label: isVerified ? 'Verified' : 'Unverified',
    statusColor: isVerified ? 'green' : 'red',
  };
  return waStatus;
};

export const getStakeholderStatus = (status: string) => {
  const isActive = status?.toLowerCase() === 'active';
  const stakeholderStatus = {
    label: isActive ? 'Active' : 'Inactive',
    color: isActive ? '#fff' : '#000',
    statusColor: isActive ? '#056DF5' : '#D6D6D6',
  };
  return stakeholderStatus;
};

export type TPaddyBuyerForm = Omit<PaddyBuyerPayload, 'bankAccountInfo'> & {
  bankAccountInfo?: {
    paymentType?: { id?: string };
    accountNumber?: string;
    accountHolderName?: string;
  };
  nationalIdPicture?: string;
  contractStartDate?: dayjs.Dayjs;
};

export const convertPaddyBuyerToForm: (
  data?: PaddyBuyer,
) => Partial<TPaddyBuyerForm> = (data) => ({
  id: data?.id,
  name: data?.name,
  phoneNumber: removePhoneCountryCode(data?.phoneNumber),
  nationalIdNumber: data?.nationalIdNumber,
  address: data?.address,
  bankAccountInfo: {
    paymentType: { id: data?.bankAccountInfos?.[0]?.paymentType?.id },
    accountNumber: data?.bankAccountInfos?.[0]?.accountNumber,
    accountHolderName: data?.bankAccountInfos?.[0]?.accountHolderName,
  },
  agronomist: data?.agronomist?.email
    ? {
        email: data.agronomist.email,
      }
    : undefined,
});

export type TFarmerGroupLeaderForm = FarmerGroupLeaderPayload & {
  nationalIdPicture?: string;
  contractStartDate?: dayjs.Dayjs;
  status?: StakeholderStatus;
};

export const convertFGLToForm: (
  data?: FarmerGroupLeader,
) => Partial<TFarmerGroupLeaderForm> = (data) => ({
  id: data?.id,
  name: data?.name,
  phoneNumber: removePhoneCountryCode(data?.phoneNumber),
  nationalIdNumber: data?.nationalIdNumber,
  address: data?.address,
  bankAccountInfo: {
    paymentType: { id: data?.bankAccountInfos?.[0]?.paymentType?.id },
    accountNumber: data?.bankAccountInfos?.[0]?.accountNumber,
    accountHolderName: data?.bankAccountInfos?.[0]?.accountHolderName,
  },
});

export const convertMachineOwnerToForm: (
  data?: MachineOwner,
) => Partial<MachineOwnerFormObject> = (data) => ({
  id: data?.id,
  name: data?.name,
  phoneNumber: removePhoneCountryCode(data?.phoneNumber),
  nationalIdNumber: data?.nationalIdNumber,
  address: data?.address,
  machinesOwned: data?.machinesOwned?.map((machine) => ({
    id: machine.id,
    machine: {
      id: machine?.machine?.id,
    },
    quantity: machine.quantity,
    age: machine.age,
    availability: machine.availability,
    pricePerHa: machine.pricePerHa?.toString(),
  })),
  preferredFarmerGroup: {
    id: data?.preferredFarmerGroup?.id,
  },
  bankAccountInfo: {
    paymentType: data?.bankAccountInfo?.paymentType?.id
      ? { id: data?.bankAccountInfo?.paymentType?.id }
      : undefined,
    accountNumber: data?.bankAccountInfo?.accountNumber,
    accountHolderName: data?.bankAccountInfo?.accountHolderName,
  },
  relatedRizeEmployee: data?.relatedRizeEmployee,
});

const removePhoneCountryCode = (phoneNumber?: string) => {
  const idIndex = phoneNumber?.indexOf('62') ?? -1;
  const vnIndex = phoneNumber?.indexOf('84') ?? -1;
  return idIndex > -1 || vnIndex > -1 ? phoneNumber?.slice(2) : phoneNumber;
};

export const validateNationalID = (
  value: string,
  entity: 'machine-owner' | 'paddy-buyers' | 'group-leader',
  country?: string,
  existingNationalId?: string,
) => {
  if (!country) return Promise.reject('Pick your Country first');

  if (!value && country !== 'Indonesia') {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    API.get(`/${entity}/national-id`, {
      params: {
        nationalId: value,
        country: country,
      },
    })
      .then((response) => {
        if (response.data?.exists) {
          if (existingNationalId && value === existingNationalId) {
            return resolve();
          }
          reject('National Id already exists');
        } else if (!response.data?.lengthSufficient) {
          reject('National Id length not correct.');
        } else if (!response.data?.allDigit) {
          reject('National Id must all digit.');
        } else {
          resolve();
        }
      })
      .catch(() => {
        reject('National Id not valid.');
      });
  });
};

export const validatePhoneNumber = (
  value: string,
  entity: 'machine-owner' | 'paddy-buyers' | 'group-leader',
  country?: string,
) => {
  if (!getCountryPhoneCode(country))
    return Promise.reject('Pick your Country first');
  return new Promise<void>((resolve, reject) => {
    API.put(`/${entity}/validate-phone-number`, {
      phone: value,
      isdCode: getCountryPhoneCode(country),
    })
      .then((response) => {
        if (response.data?.isValid) {
          resolve();
          return;
        }
        if (response.data?.errorCode) {
          const key = response.data
            .errorCode as keyof typeof VALIDATION_ERROR_CODES;
          reject(VALIDATION_ERROR_CODES[key]);
          return;
        }
        reject('Phone Number is not valid.');
      })
      .catch((error) => {
        if (error.response?.data?.title) {
          reject(error.response.data.title);
        } else {
          reject('Phone Number is not valid.');
        }
      });
  });
};

export const getStakeholderProperties = (location: string, country: string) => {
  switch (location) {
    case routes.paddyBuyer:
    case routes.stakeholders:
    default:
      return {
        searchPlaceholder: t('common.search', {
          value: getStakeholderTypeForCountry(country, 'PB')?.toLowerCase(),
        }),
      };
    case routes.farmerGroupLeader:
      return {
        searchPlaceholder: t('stakeholders.filters.searchFgl'),
      };
    case routes.machineOwner:
      return {
        searchPlaceholder: t('stakeholders.filters.searchMachineOwner'),
      };
  }
};

export const STAKEHOLDER_STATUS_OPTIONS = [
  { label: 'Active', value: 'Active' },
  { label: 'Inactive', value: 'INACTIVE' },
];

export const SCROLL_OPTIONS: ScrollFocusOptions = {
  behavior: 'smooth',
};

export const validateFiles = (options: {
  isEdit: boolean;
  country: string;
  firstPageContract?: Blob;
  contractPictures: Blob[];
  nationalIdPictures: Blob[];
  uploadedFirstPageContract?: UploadFile;
  existingContractPictures: UploadFile[];
  existingNationalIdPictures: UploadFile[];
  deletedContractPictures: UploadFile[];
  deletedIdPictures: UploadFile[];
}): boolean => {
  const {
    isEdit,
    country,
    firstPageContract,
    contractPictures,
    nationalIdPictures,
    uploadedFirstPageContract,
    existingContractPictures,
    existingNationalIdPictures,
    deletedContractPictures,
    deletedIdPictures,
  } = options;

  // Skip validation for Vietnam
  if (country === 'Vietnam') return true;

  // For new entries
  if (!isEdit) {
    const requiredFilesMissing =
      !firstPageContract ||
      contractPictures.length === 0 ||
      nationalIdPictures.length === 0;

    if (requiredFilesMissing) {
      message.error(
        'Please add first page contract, at least one Signed Contract and one National ID picture.',
      );
      return false;
    }
    return true;
  }

  // For edits
  const isFirstPageDeleted =
    uploadedFirstPageContract &&
    deletedContractPictures.some(
      (file) => file.uid === uploadedFirstPageContract.uid,
    );
  const isFirstPageMissing = !firstPageContract && isFirstPageDeleted;

  const remainingContracts =
    existingContractPictures.filter(
      (file) =>
        !deletedContractPictures.some((deleted) => deleted.uid === file.uid),
    ).length + contractPictures.length;

  const remainingIds =
    existingNationalIdPictures.filter(
      (file) => !deletedIdPictures.some((deleted) => deleted.uid === file.uid),
    ).length + nationalIdPictures.length;

  if (isFirstPageMissing || remainingContracts === 0 || remainingIds === 0) {
    message.error(
      'You cannot leave contract, first page contract, or national ID pictures empty.',
    );
    return false;
  }

  return true;
};
